import { DependencyList, RefObject } from "react";
import { useLayoutEffect, useRef, useState } from "react";

/** Default bottom content padding (in px) used in dynamic height calculations. */
const DEFAULT_DYNAMIC_HEIGHT_CONTENT_PADDING = 30;

/** Default container border size (in px) used in dynamic height calculations. */
const DEFAULT_DYNAMIC_HEIGHT_BORDER = 2;

/** Combined default bottom offset (padding + border, in px) for dynamic height. */
const DEFAULT_DYNAMIC_HEIGHT_BOTTOM_OFFSET =
  DEFAULT_DYNAMIC_HEIGHT_CONTENT_PADDING + DEFAULT_DYNAMIC_HEIGHT_BORDER;

interface UseDynamicHeightOptions {
  /**
   * When `true`, skips all height calculation and returns `undefined`.
   * @default false
   */
  shouldSkip?: boolean;

  /**
   * Optional dependency list to force recalculation when specific values change
   * (e.g. loading flags or number of rendered items).
   */
  deps?: DependencyList;
}

interface UseDynamicHeightResult {
  /** Attach this ref to the container whose top position anchors the height calculation */
  ref: RefObject<HTMLDivElement | null>;

  /**
   * CSS `calc()` string that makes the container fill the remaining viewport height,
   * accounting for the default bottom offset
   */
  height?: string;
}

/**
 * Dynamically calculates the height a container should occupy to fill the remaining
 * viewport space below its current top position.
 */
export function useDynamicHeight({
  shouldSkip = false,
  deps = [],
}: UseDynamicHeightOptions = {}): UseDynamicHeightResult {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string | undefined>();

  useLayoutEffect(() => {
    if (!ref.current || shouldSkip) {
      return undefined;
    }

    function updateHeight(): void {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      setHeight(
        `calc(100dvh - (${rect.top}px + ${DEFAULT_DYNAMIC_HEIGHT_BOTTOM_OFFSET}px))`
      );
    }

    // Run after layout, and schedule again on the next frame to catch late DOM updates (e.g., banners)
    const requestAnimationFrameId = requestAnimationFrame(updateHeight);

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(document.body);

    window.addEventListener("resize", updateHeight);

    // Recalculate immediately to cover state-driven layout changes above the container
    updateHeight();

    return () => {
      cancelAnimationFrame(requestAnimationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
    // `deps` is intentionally spread so consumers can opt into recalculation triggers.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkip, ...deps]);

  return { ref, height };
}
